import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  FavoriteDocument,
  getFavoritesStore,
  resolveFavoritesForActiveDocuments,
} from 'features/knowledgeDocumentViewer/utils/favoriteDocumentsStorage'
import { useGeckoPermissions } from 'features/permissions'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { RolodexEntryType } from 'types'

function documentTableRowToFavorite(doc: DocumentTableRow): FavoriteDocument {
  return {
    id: doc.id,
    title: doc.title,
    filename: doc.filename,
    documentTitle: doc.documentTitle,
    timestamp: Date.now(),
  }
}

const EMPTY: FavoriteDocument[] = []
const NOOP_SUBSCRIBE = () => () => {}
const NOOP_SNAPSHOT = () => EMPTY

export interface UseFavoriteDocumentsReturn {
  favoriteDocuments: FavoriteDocument[]
  isFavorited: (id: string) => boolean
  toggleFavorite: (doc: DocumentTableRow) => void
  removeFavorite: (id: string) => void
}

export function useFavoriteDocuments(
  siteId?: string
): UseFavoriteDocumentsReturn {
  const { user } = useGeckoPermissions()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()
  const userId = user?.id

  const store = useMemo(() => {
    if (!userId || !siteId) return null
    return getFavoritesStore(userId, siteId)
  }, [userId, siteId])

  const favoriteDocuments = useSyncExternalStore(
    store?.subscribe ?? NOOP_SUBSCRIBE,
    store?.getStableSnapshot ?? NOOP_SNAPSHOT
  )

  const favoriteDocumentIds = useMemo(
    () => Array.from(new Set(favoriteDocuments.map((favorite) => favorite.id))),
    [favoriteDocuments]
  )

  const activeDocumentLoadOptions = useMemo(
    () =>
      !isPipelineConfigScopeLoading && favoriteDocumentIds.length > 0
        ? {
            type: [RolodexEntryType.SYSTEM_DOCUMENT],
            ids: favoriteDocumentIds,
            tags: getPipelineConfigScopedTags({ site: siteId ?? '' }),
            limit: favoriteDocumentIds.length,
          }
        : undefined,
    [
      favoriteDocumentIds,
      getPipelineConfigScopedTags,
      isPipelineConfigScopeLoading,
      siteId,
    ]
  )

  const { data: activeDocuments } = useGetRolodexEntriesByLoadOptions<
    SystemDocumentEntry[]
  >(activeDocumentLoadOptions)

  const activeFavoriteDocuments = useMemo(
    () =>
      resolveFavoritesForActiveDocuments({
        favoriteDocuments,
        activeDocuments: activeDocuments ?? [],
      }),
    [activeDocuments, favoriteDocuments]
  )

  const favoritedIds = useMemo(
    () => new Set(favoriteDocuments.map((f) => f.id)),
    [favoriteDocuments]
  )

  const isFavorited = useCallback(
    (id: string) => favoritedIds.has(id),
    [favoritedIds]
  )

  const toggleFavorite = useCallback(
    (doc: DocumentTableRow) => {
      if (!store) return
      const current = store.read()
      const exists = current.some((f) => f.id === doc.id)
      if (exists) {
        store.write(current.filter((f) => f.id !== doc.id))
      } else {
        store.write([documentTableRowToFavorite(doc), ...current])
      }
    },
    [store]
  )

  const removeFavorite = useCallback(
    (id: string) => {
      if (!store) return
      store.write(store.read().filter((f) => f.id !== id))
    },
    [store]
  )

  return {
    favoriteDocuments: activeFavoriteDocuments,
    isFavorited,
    toggleFavorite,
    removeFavorite,
  }
}
